"use client";

import useSearchRouter, { ModifySearchParams } from "@/app/hooks/useSearchRouter";
import { STAR_RATING_ATTRIBUTE_NAME } from "@/app/search/facets/facetBar";
import Checkbox from "@/components/design-system/checkbox";
import StarRating from "@/components/design-system/star-rating";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import style from "./genericFacet.module.scss";

export const possibleStarRatings = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
const ratingsToDisplay = [1, 2, 3, 4];

export default function RatingFacet({ values, indexName }: RatingFacetProps) {
  const [selectedRating, setSelectedRating] = useState<number | undefined>();
  const { modifyFacets, isFacetActive } = useSearchRouter(indexName);
  const ratingValues = Object.entries(values);

  const sortedItems = useMemo(() => {
    // even when star ratings are not returned to us because they didn't have any hits, we still want to display all 4 star rating groups
    const missingRatings = ratingsToDisplay.filter((rating) => {
      const ratingFromRecord = ratingValues.find((ratingRecord) => {
        return ratingRecord[0] === rating.toString();
      });
      return ratingFromRecord === undefined;
    });

    const fullRatings = [...ratingValues];
    missingRatings.forEach((rating) => {
      fullRatings.push([rating.toString(), 0]);
    });
    const sortedValues = fullRatings.sort((a, b) => +Number(b[0]) - +Number(a[0]));
    const starRatings: { value: number; count: number; checked: boolean }[] = [];

    sortedValues.forEach((value, index) => {
      const ratingNum = Number(value[0]);
      const count = value[1];

      starRatings.push({
        value: ratingNum,
        count: index === 0 ? count : starRatings[index - 1].count + count,
        checked: selectedRating === ratingNum,
      });
    });

    const filteredRatings = starRatings.filter((rating) => ratingsToDisplay.includes(rating.value));

    return filteredRatings;
  }, [ratingValues, selectedRating]);

  const totalRatings = useMemo(() => {
    return ratingValues
      .sort((a, b) => +Number(b[0]) - +Number(a[0]))
      .map((item) => item[1])
      .reduce((previousValue: number, currentValue: number) => previousValue + currentValue);
  }, [ratingValues]);

  const isRatingFacetSelected = useCallback(
    (starRating: number) => {
      // we apply all ratings from the selected rating up to the max of 5 stars,
      // so we can ONLY apply the selected state to the LOWEST star rating

      let lowestActiveRating = 5;
      ratingsToDisplay.forEach((rating) => {
        const ratingActive = isFacetActive(STAR_RATING_ATTRIBUTE_NAME, rating.toString());

        if (ratingActive && rating < lowestActiveRating) {
          lowestActiveRating = rating;
        }
      });

      return lowestActiveRating === starRating;
    },
    [isFacetActive],
  );

  const onClickCallback = useCallback(
    (event: ChangeEvent<HTMLInputElement>, starRating: number) => {
      const isCurrentlySelected = isRatingFacetSelected(starRating);

      if (isCurrentlySelected) {
        // If already selected, uncheck it (remove all rating filters)
        setSelectedRating(undefined);
        const starFacets: ModifySearchParams[] = possibleStarRatings.map((rating) => ({
          facetName: STAR_RATING_ATTRIBUTE_NAME,
          facetValue: rating.toString(),
          deleteFacet: true,
        }));
        modifyFacets(starFacets);
      } else {
        // If not selected, check it (apply rating filter)
        setSelectedRating(starRating);
        const starFacets: ModifySearchParams[] = possibleStarRatings.map((rating) => ({
          facetName: STAR_RATING_ATTRIBUTE_NAME,
          facetValue: rating.toString(),
          deleteFacet: !(rating === starRating),
        }));
        modifyFacets(starFacets);
      }
    },
    [modifyFacets, isRatingFacetSelected],
  );

  if (totalRatings === 0) {
    return null;
  }

  return (
    <div className={style.facet}>
      <div className={style.name}>Rating</div>
      <ul className={style.facetList}>
        {sortedItems.map((item) => {
          const selectedFacet = isRatingFacetSelected(item.value);
          return (
            <li className={style.facetValue} key={item.value}>
              <div className={style.facetCheckboxLabel}>
                <Checkbox
                  id={`star-check-${item.value}`}
                  checked={selectedFacet}
                  size="small"
                  showLabel={false}
                  aria-label={`${item.value} stars and up`}
                  onChange={(event) => onClickCallback(event, item.value)}
                />
                <div className={style.starContainer}>
                  <StarRating rating={item.value} size="xs" />
                </div>
                <span className={`${style.ratingUpText} ${selectedFacet ? style.selected : ""}`}>& Up</span>
                <span className={`${style.ratingCount} ${style.count}`}>{item.count}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export type RatingFacetProps = {
  values: Record<string, number>;
  indexName?: string;
};
